import React, { useState } from 'react'
import { ExpandableRow } from 'transfix-ui/components/ExpandableRow'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, Button, makeStyles, Typography } from 'transfix-ui/core'
import { EditIcon } from 'transfix-icons'
import { IconButton } from 'transfix-ui'
import { useModal } from 'utils/hooks'
import { SaveParserModal } from '../SaveParserModal'
import { Parser, ParserVersion } from '../types'
import {
  ParserVersionConfirmationModal,
  TParserVersionModalType,
} from './ParserVersionConfirmationModal'
import { ParserVersionsTable } from './ParserVersionsTable'

interface IParserVersionModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onClose: () => void
  parsers: Parser[]
  onParserVersionSelect: (parserVersion: ParserVersion) => void
  onSuccess: () => void
}

export type SaveParserModal = 'SaveParserModal'
export type ParserVersionConfirmationModal = 'parserVersionConfirmationModal'

const useParserVersionModalStyles = makeStyles(theme => ({
  button: {
    float: 'right',
    marginBottom: theme.spacing(1),
  },
}))

export const ParserVersionModal = ({
  onParserVersionSelect,
  onSuccess,
  parsers,
  ...props
}: IParserVersionModalProps) => {
  const actions: IModalBaseProps['actions'] = []

  const classes = useParserVersionModalStyles()

  const { closeModal, isOpen, openModal } = useModal<SaveParserModal>()

  const [selectedParserVersion, setSelectedParserVersion] = useState<ParserVersion>()
  const [selectedParser, setSelectedParser] = useState<Parser>()
  const [modalType, setModalType] = useState<TParserVersionModalType>('create')

  const {
    closeModal: closeParserVersionConfirmationModal,
    isOpen: isParserVersionConfirmationModalOpen,
    openModal: openParserVersionConfirmationModal,
  } = useModal<ParserVersionConfirmationModal>()

  const openSaveParserModal = openModal('SaveParserModal')

  const openParserVersionModal = openParserVersionConfirmationModal(
    'parserVersionConfirmationModal'
  )

  const closeHandler = () => {
    setSelectedParserVersion(undefined)
    closeParserVersionConfirmationModal()
  }

  const parserVersionDeleteHandler = (parserVersion: ParserVersion) => {
    setModalType('delete')
    setSelectedParserVersion(parserVersion)
    openParserVersionModal()
  }

  const parserVersionCopyHandler = (parserVersion: ParserVersion) => {
    setModalType('copy')
    setSelectedParserVersion(parserVersion)
    openParserVersionModal()
  }

  const createNewVersionHandler = (parser: Parser) => {
    setModalType('create')
    setSelectedParser(parser)
    openParserVersionModal()
  }

  const newParserHandler = () => {
    setSelectedParser(undefined)
    openSaveParserModal()
  }

  const editParserHandler = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    parser: Parser
  ) => {
    setSelectedParser(parser)
    e.stopPropagation()
    openSaveParserModal()
  }

  return (
    <ModalBase
      dataTest='ParserVersionModal'
      modalType={MODAL_TYPES.form}
      size={7}
      open
      actions={actions}
      title={<Typography variant='h3'>Select A Parser Version To Edit</Typography>}
      header={
        <Button onClick={newParserHandler} className={classes.button}>
          Create New Parser
        </Button>
      }
      closeOnBackdropClick
      {...props}>
      <Box paddingBottom='50px'>
        {parsers.map(parser => (
          <Box mt={2} key={`${parser.id}-${parser.name}`}>
            <ExpandableRow
              row={
                <Box alignItems='center' display='flex'>
                  <Typography variant='h4'>{parser.name}</Typography>
                  <IconButton
                    Icon={EditIcon}
                    color='tertiary'
                    size='small'
                    onClick={e => editParserHandler(e, parser)}
                    data-test='edit-accounting'
                    label='Edit Parser'
                  />
                </Box>
              }
              rowClass='card'
              onExpand={() => {}}
              details={
                <Box>
                  <Button
                    className={classes.button}
                    onClick={() => {
                      createNewVersionHandler(parser)
                    }}>
                    Create New Version
                  </Button>
                  <ParserVersionsTable
                    parser={parser}
                    onParserVersionSelect={onParserVersionSelect}
                    onParserVersionDelete={parserVersionDeleteHandler}
                    onParserVersionCopy={parserVersionCopyHandler}
                  />
                </Box>
              }
            />
          </Box>
        ))}
      </Box>
      {isOpen('SaveParserModal') && (
        <SaveParserModal
          onClose={closeModal}
          onSuccess={onSuccess}
          selectedParser={selectedParser}
        />
      )}
      {isParserVersionConfirmationModalOpen('parserVersionConfirmationModal') && (
        <ParserVersionConfirmationModal
          onClose={closeHandler}
          onSuccess={onSuccess}
          selectedParser={selectedParser}
          selectedParserVersion={selectedParserVersion}
          setSelectedParserVersion={setSelectedParserVersion}
          type={modalType}
        />
      )}
    </ModalBase>
  )
}
